import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { WorkflowBuilderStoreService } from '../../services/workflow-builder-store.service';
import {
  IAuthorisation,
  IStateMeta,
} from '../../models/workflow-builder.model';
import { EVENTS } from '../../core/catalogs';

const WHO_AUTH: Record<string, IAuthorisation> = {
  Applicant: {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_CITIZEN'],
    authorisedUsers: null,
  },
  Officer: {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_OFFICER'],
    authorisedUsers: null,
  },
  'External system': {
    systemAuthorised: false,
    authorisedRoles: ['ROLE_EXTERNAL_SYSTEM_*'],
    authorisedUsers: null,
  },
  System: {
    systemAuthorised: true,
    authorisedRoles: null,
    authorisedUsers: null,
  },
};

/** Inline composer to add a new event (transition) from a state. */
@Component({
  selector: 'irembogov-iwb-add-event',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="composer addinline">
      <button
        *ngIf="clipboardEvent && !lockEvent"
        class="btn pri iwb-paste-btn"
        style="width:100%;margin-bottom:10px"
        (click)="pasteEvent()">
        <i class="fa-solid fa-paste"></i> Paste event "{{ clipboardEvent }}"
        <small style="opacity:.85">(⌘V)</small>
      </button>
      <div class="q">
        1 · What happens? <small>the event that moves the application</small>
      </div>
      <input *ngIf="lockEvent" class="inp" [value]="lockEvent" disabled />
      <select *ngIf="!lockEvent" class="selx" [(ngModel)]="event">
        <option *ngFor="let e of events" [value]="e">{{ e }}</option>
      </select>

      <div class="q">2 · Who triggers it?</div>
      <div class="chips">
        <span
          class="chip"
          *ngFor="let w of whoOptions"
          [class.on]="who === w"
          (click)="who = w"
          >{{ w }}</span
        >
      </div>
      <div class="authnote">{{ authNote }}</div>

      <div class="q">3 · It moves the application to:</div>
      <ng-select
        class="iwb-dest"
        [items]="destOptions"
        bindValue="code"
        [(ngModel)]="dest"
        [searchable]="true"
        [clearable]="false"
        [searchFn]="searchState"
        placeholder="Search by state code or name…"
        dropdownPosition="auto">
        <ng-template ng-label-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <ng-container *ngIf="item.code === '__new'">{{
            item.name
          }}</ng-container>
          <span *ngIf="item.code !== '__new'" class="iwb-opt-text">
            <span class="iwb-opt-code">{{ item.code }}</span>
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code">
              — {{ item.name }}</small
            >
          </span>
        </ng-template>
        <ng-template ng-option-tmp let-item="item">
          <span
            class="iwb-dot"
            [style.background]="item.colour || 'transparent'"></span>
          <ng-container *ngIf="item.code === '__new'">{{
            item.name
          }}</ng-container>
          <span *ngIf="item.code !== '__new'" class="iwb-opt-text">
            <small
              class="iwb-opt-name"
              *ngIf="item.name && item.name !== item.code"
              >{{ item.name }}</small
            >
            <span class="iwb-opt-code">{{ item.code }}</span>
          </span>
        </ng-template>
      </ng-select>
      <div class="newstatus" [class.show]="dest === '__new'">
        <div class="row2">
          <input class="inp" [(ngModel)]="newLabel" placeholder="State name" />
          <input class="inp" [(ngModel)]="newCode" placeholder="CODE" />
        </div>
      </div>

      <div
        class="aerr"
        *ngIf="error"
        style="color:#b42318;font-size:12px;margin:6px 0">
        {{ error }}
      </div>
      <div class="go">
        <button class="btn pri" (click)="commit()">Add event</button>
        <button class="btn" (click)="cancelled.emit()">Cancel</button>
      </div>
    </div>
  `,
})
export class AddEventComponent {
  @Input() fromState = '';
  @Input() states: IStateMeta[] = [];
  /** Full application-state catalog from the API (preferred over the workflow's own states). */
  @Input() apiStates: IStateMeta[] = [];
  /** When set, the event is fixed (no picker) — used for the mandatory first NEW → APPLY event. */
  @Input() lockEvent = '';
  @Output() done = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  private _destBase: IStateMeta[] | null = null;
  private _destOptions: Array<IStateMeta & { code: string }> = [];
  /**
   * States offered in the destination picker: API catalog if available, else the workflow's own.
   * Memoised to a STABLE array reference — ng-select rebuilds its option list whenever `[items]`
   * changes identity, and under OnPush a fresh array each change-detection would recreate the
   * options mid-interaction and drop the user's click. Recompute only when the source changes.
   */
  get destOptions(): Array<IStateMeta & { code: string }> {
    const base = this.apiStates.length ? this.apiStates : this.states;
    if (base !== this._destBase) {
      this._destBase = base;
      this._destOptions = [
        ...base,
        {
          code: '__new',
          name: '+ Create a new state…',
          colour: 'transparent',
        } as IStateMeta,
      ];
    }
    return this._destOptions;
  }

  readonly events = EVENTS;
  readonly whoOptions = Object.keys(WHO_AUTH);
  event = EVENTS[0];
  who = 'Applicant';
  dest = '';
  newLabel = '';
  newCode = '';
  error = '';

  constructor(private store: WorkflowBuilderStoreService) {}

  /** ng-select filter: match the typed term against either the state code OR its name. */
  searchState = (
    term: string,
    item: IStateMeta & { code: string }
  ): boolean => {
    const t = (term || '').toLowerCase();
    return (
      (item.code || '').toLowerCase().includes(t) ||
      (item.name || '').toLowerCase().includes(t)
    );
  };

  /** Event name on the clipboard (shows the Paste button), or null. */
  get clipboardEvent(): string | null {
    return this.store.clipboardEvent;
  }

  /** Apply the copied event's full config onto this composer's source state, then close. */
  pasteEvent(): void {
    const ev = this.store.clipboardEvent;
    if (!ev) return;
    if (this.store.pasteEventOnto(this.fromState)) this.done.emit();
    else
      this.error = `"${this.fromState}" already has a "${ev}" event — pick a different event.`;
  }

  /** ⌘V / Ctrl+V while the composer is open pastes the copied event (ignored while typing in a field). */
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!(e.metaKey || e.ctrlKey) || (e.key !== 'v' && e.key !== 'V')) return;
    const t = e.target as HTMLElement | null;
    if (
      t &&
      typeof t.closest === 'function' &&
      t.closest('input, textarea, select, [contenteditable]')
    )
      return; // let normal text paste happen
    if (!this.store.clipboardEvent) return;
    e.preventDefault();
    this.pasteEvent();
  }

  // The destination starts empty on purpose: the searchable picker shows its placeholder and
  // the user actively chooses (with 400+ backend states, auto-selecting the first is a footgun).

  get authNote(): string {
    const a = WHO_AUTH[this.who];
    return a.systemAuthorised
      ? 'Automatic — the system performs it. No role is added.'
      : 'Not automatic — role required: ' +
          (a.authorisedRoles || []).join(', ');
  }

  /** Normalise a free-typed label/code into a state code (UPPER_SNAKE). */
  private toCode(raw: string): string {
    return raw
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_');
  }

  commit(): void {
    this.error = '';
    const event = (this.lockEvent || this.event)
      .toUpperCase()
      .replace(/\s+/g, '_');
    let toStateCode = this.dest;
    if (this.dest === '__new') {
      toStateCode = this.toCode(this.newCode || this.newLabel);
      if (!toStateCode) {
        this.error = 'Enter a name or code for the new state.';
        return;
      }
    }
    if (!event) {
      this.error = 'Choose an event.';
      return;
    }
    if (!toStateCode) {
      this.error = 'Choose a destination state.';
      return;
    }
    // addTransition is a no-op when (startState, event) already exists — surface that
    // instead of silently closing, so "add a new state" never appears to do nothing.
    const added = this.store.addTransition({
      startState: this.fromState,
      event,
      toStateCode,
      auth: WHO_AUTH[this.who],
    });
    if (!added) {
      this.error = `"${this.fromState}" already has a "${event}" event — pick a different event.`;
      return;
    }
    this.done.emit();
  }
}
